import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { AppLayout } from '@/components/feature/AppLayout';

interface ProjectData {
  id: string;
  name: string;
  description: string | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
  scrum_active: boolean;
  _client_name: string;
  _task_total: number;
  _task_done: number;
  _task_doing: number;
  _task_backlog: number;
  _value: number;
  _paid: number;
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  proposta: { label: 'Proposta', color: 'bg-slate-100 text-slate-600' },
  ativo: { label: 'Ativo', color: 'bg-emerald-100 text-emerald-700' },
  pausado: { label: 'Pausado', color: 'bg-amber-100 text-amber-700' },
  concluido: { label: 'Concluído', color: 'bg-cyan-100 text-cyan-700' },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-600' },
};

export default function UserDashboardPage() {
  const { user, profile } = useAuthContext();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    // Buscar projetos via project_members
    const { data: memberships } = await supabase
      .from('project_members')
      .select('project_id')
      .eq('user_id', user.id);

    const memberProjectIds = (memberships || []).map(m => m.project_id);

    // Buscar projetos via client_id do perfil do usuário
    const { data: userProfile } = await supabase
      .from('profiles')
      .select('client_id')
      .eq('id', user.id)
      .maybeSingle();

    let clientProjectIds: string[] = [];
    if (userProfile?.client_id) {
      const { data: clientProjs } = await supabase
        .from('projects')
        .select('id')
        .eq('client_id', userProfile.client_id);
      clientProjectIds = (clientProjs || []).map(p => p.id);
    }

    // Unir os dois conjuntos de IDs sem duplicatas
    const projectIds = [...new Set([...memberProjectIds, ...clientProjectIds])];

    if (projectIds.length === 0) {
      setProjects([]);
      setLoading(false);
      return;
    }

    const { data: projs } = await supabase
      .from('projects')
      .select('id, name, description, status, start_date, end_date, scrum_active, client_id')
      .in('id', projectIds)
      .order('name');

    if (!projs || projs.length === 0) {
      setProjects([]);
      setLoading(false);
      return;
    }

    const clientIds = [...new Set(projs.map(p => p.client_id).filter(Boolean))];
    const [clientsRes, tasksRes] = await Promise.all([
      clientIds.length > 0
        ? supabase.from('clients').select('id, name').in('id', clientIds)
        : { data: [] },
      supabase.from('tasks').select('project_id, board_status, value, paid').in('project_id', projectIds),
    ]);

    const cMap = Object.fromEntries((clientsRes.data || []).map((c: { id: string; name: string }) => [c.id, c.name]));
    const allTasks = tasksRes.data || [];

    const enriched: ProjectData[] = projs.map(p => {
      const ptasks = allTasks.filter(t => t.project_id === p.id);
      return {
        id: p.id,
        name: p.name,
        description: p.description,
        status: p.status,
        start_date: p.start_date,
        end_date: p.end_date,
        scrum_active: p.scrum_active,
        _client_name: p.client_id ? cMap[p.client_id] || '—' : '—',
        _task_total: ptasks.length,
        _task_done: ptasks.filter(t => t.board_status === 'done').length,
        _task_doing: ptasks.filter(t => t.board_status === 'doing').length,
        _task_backlog: ptasks.filter(t => t.board_status === 'backlog').length,
        _value: ptasks.reduce((s, t) => s + Number(t.value || 0), 0),
        _paid: ptasks.filter(t => t.paid).reduce((s, t) => s + Number(t.value || 0), 0),
      };
    });

    setProjects(enriched);
    setLoading(false);
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const fmtDate = (d: string | null) => d ? new Date(d + 'T12:00:00').toLocaleDateString('pt-BR') : '—';

  const totalValue = projects.reduce((s, p) => s + p._value, 0);
  const totalPaid = projects.reduce((s, p) => s + p._paid, 0);
  const totalTasks = projects.reduce((s, p) => s + p._task_total, 0);
  const totalDone = projects.reduce((s, p) => s + p._task_done, 0);

  return (
    <AppLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-slate-800 text-xl font-bold">
          Olá, {profile?.full_name?.split(' ')[0] || 'bem-vindo'}!
        </h1>
        <p className="text-slate-400 text-sm mt-0.5">Acompanhe seus projetos e atividades</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-cyan-500 text-2xl"></i>
        </div>
      ) : projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-slate-100 mb-4">
            <i className="ri-folder-3-line text-slate-400 text-2xl"></i>
          </div>
          <p className="text-slate-700 font-semibold">Nenhum projeto vinculado</p>
          <p className="text-slate-400 text-sm mt-1">Aguarde o administrador vincular você a um projeto.</p>
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-cyan-50 mb-3">
                <i className="ri-folder-line text-cyan-500 text-base"></i>
              </div>
              <p className="text-slate-400 text-xs">Projetos</p>
              <p className="text-slate-800 text-2xl font-bold mt-0.5">{projects.length}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-violet-50 mb-3">
                <i className="ri-task-line text-violet-500 text-base"></i>
              </div>
              <p className="text-slate-400 text-xs">Atividades</p>
              <p className="text-slate-800 text-2xl font-bold mt-0.5">{totalTasks}</p>
              <p className="text-slate-400 text-xs mt-0.5">{totalDone} concluídas</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-50 mb-3">
                <i className="ri-money-dollar-circle-line text-emerald-500 text-base"></i>
              </div>
              <p className="text-slate-400 text-xs">Valor Pago</p>
              <p className="text-emerald-600 text-xl font-bold mt-0.5">{fmt(totalPaid)}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-amber-50 mb-3">
                <i className="ri-time-line text-amber-500 text-base"></i>
              </div>
              <p className="text-slate-400 text-xs">Pendente</p>
              <p className="text-amber-600 text-xl font-bold mt-0.5">{fmt(totalValue - totalPaid)}</p>
            </div>
          </div>

          {/* Lista de projetos */}
          <div className="space-y-3">
            {projects.map(p => {
              const st = STATUS_CONFIG[p.status] || { label: p.status, color: 'bg-slate-100 text-slate-600' };
              const progress = p._task_total > 0 ? Math.round((p._task_done / p._task_total) * 100) : 0;
              return (
                <div
                  key={p.id}
                  onClick={() => navigate(`/app/projeto/${p.id}`)}
                  className="bg-white rounded-xl border border-slate-200 p-5 hover:border-cyan-300 transition-all cursor-pointer group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-cyan-50 border border-cyan-100 shrink-0 group-hover:bg-cyan-100 transition-colors">
                        <i className="ri-folder-3-line text-cyan-500 text-base"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-slate-800 font-semibold text-sm">{p.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>
                          {p.scrum_active && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700">Scrum</span>
                          )}
                        </div>
                        <p className="text-slate-400 text-xs mt-0.5">{p._client_name}</p>
                        {p.description && (
                          <p className="text-slate-500 text-xs mt-1 truncate">{p.description}</p>
                        )}
                        {/* Barra de progresso */}
                        {p._task_total > 0 && (
                          <div className="mt-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-slate-400 text-xs">{p._task_done}/{p._task_total} concluídas</span>
                              <span className="text-slate-500 text-xs font-medium">{progress}%</span>
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-cyan-400 rounded-full transition-all"
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <div className="flex items-center gap-3 text-xs text-slate-400">
                        <span><i className="ri-calendar-line mr-1"></i>{fmtDate(p.start_date)}</span>
                        {p.end_date && <span><i className="ri-flag-line mr-1"></i>{fmtDate(p.end_date)}</span>}
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-slate-500"><i className="ri-task-line mr-1"></i>{p._task_doing} em andamento</span>
                        <span className="text-slate-400"><i className="ri-archive-line mr-1"></i>{p._task_backlog} backlog</span>
                      </div>
                      {p._value > 0 && (
                        <div className="text-right">
                          <p className="text-emerald-600 text-xs font-medium">{fmt(p._paid)} pago</p>
                          {p._value - p._paid > 0 && (
                            <p className="text-amber-500 text-xs">{fmt(p._value - p._paid)} pendente</p>
                          )}
                        </div>
                      )}
                      <i className="ri-arrow-right-line text-slate-300 group-hover:text-cyan-400 transition-colors text-base"></i>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </AppLayout>
  );
}